import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { AlertColor } from '@mui/material/Alert';
import { ClassNameMap, CombinedClassKey, SnackbarKey, SnackbarMessage, useSnackbar, VariantType } from 'notistack';
import React from 'react';
import { TypeOptions } from 'react-toastify';

import './D1Toast.scss';

export type D1ToastVariant = 'success' | 'info' | 'warning' | 'error';

type ToastVariantInput = AlertColor | TypeOptions | VariantType | undefined;

const variantLabels: Record<D1ToastVariant, string> = {
  success: 'Success',
  info: 'Info',
  warning: 'Warning',
  error: 'Error',
};

export const normalizeD1ToastVariant = (variant: ToastVariantInput): D1ToastVariant => {
  if (variant === 'success' || variant === 'warning' || variant === 'error' || variant === 'info') {
    return variant;
  }

  return 'info';
};

interface D1ToastIconProps {
  variant: ToastVariantInput;
}

export const D1ToastIcon = ({ variant }: D1ToastIconProps): JSX.Element => {
  const normalizedVariant = normalizeD1ToastVariant(variant);

  switch (normalizedVariant) {
    case 'success':
      return <CheckCircleOutlineIcon aria-hidden="true" />;
    case 'warning':
      return <WarningAmberOutlinedIcon aria-hidden="true" />;
    case 'error':
      return <HighlightOffIcon aria-hidden="true" />;
    case 'info':
    default:
      return <InfoOutlinedIcon aria-hidden="true" />;
  }
};

interface D1ToastCloseButtonProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  variant?: ToastVariantInput;
}

export const D1ToastCloseButton = ({ onClick, variant }: D1ToastCloseButtonProps): JSX.Element => (
  <button
    type="button"
    className="d1-toast__close"
    aria-label={`Dismiss ${variantLabels[normalizeD1ToastVariant(variant)].toLowerCase()} notification`}
    onClick={onClick}
  >
    <CloseIcon aria-hidden="true" />
  </button>
);

interface D1ToastContentProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  message?: SnackbarMessage;
  onClose?: () => void;
  variant?: ToastVariantInput;
}

export const D1ToastContent = React.forwardRef<HTMLDivElement, D1ToastContentProps>(
  ({ className = '', message, onClose, role = 'alert', variant, ...rest }, ref) => {
    const normalizedVariant = normalizeD1ToastVariant(variant);

    return (
      <div
        {...rest}
        ref={ref}
        role={role}
        aria-label={`${variantLabels[normalizedVariant]} notification`}
        className={`d1-toast d1-toast--${normalizedVariant} ${className}`.trim()}
      >
        <div className="d1-toast__content">
          <span className="d1-toast__icon">
            <D1ToastIcon variant={normalizedVariant} />
          </span>
          <div className="d1-toast__message">{message}</div>
          {onClose && (
            <D1ToastCloseButton
              variant={normalizedVariant}
              onClick={(event) => {
                event.stopPropagation();
                onClose();
              }}
            />
          )}
        </div>
      </div>
    );
  },
);

D1ToastContent.displayName = 'D1ToastContent';

interface D1NotistackToastProps {
  className?: string;
  id: SnackbarKey;
  message?: SnackbarMessage;
  style?: React.CSSProperties;
  variant?: VariantType;
}

export const D1NotistackToast = React.forwardRef<HTMLDivElement, D1NotistackToastProps>(
  ({ className, id, message, style, variant }, ref) => {
    const { closeSnackbar } = useSnackbar();

    return (
      <D1ToastContent
        ref={ref}
        className={className}
        message={message}
        onClose={() => closeSnackbar(id)}
        style={style}
        variant={variant}
      />
    );
  },
);

D1NotistackToast.displayName = 'D1NotistackToast';

export const d1NotistackClasses: Partial<ClassNameMap<CombinedClassKey>> = {
  containerAnchorOriginBottomCenter: 'd1-toast-notistack-container',
};
